<script>
// Stolen and improved from vue-text-highlight
// Now supports html strings
import cloneRegexp from 'clone-regexp'
import diacritics from 'diacritics'

const isDigit = char => /^\d+$/.test(char)
const isLetter = char => char.toUpperCase() !== char.toLowerCase() || char.codePointAt(0) > 127
const isLetterOrDigit = char => isLetter(char) || isDigit(char)

function indicesOf(
  text,
  searchStringOrRegex,
  { caseSensitive = false, diacriticsSensitive = false, wholeWordMatch = false } = {},
) {
  if (searchStringOrRegex instanceof RegExp) {
    const re = cloneRegexp(searchStringOrRegex, { global: true })
    const indices = []

    let match = re.exec(text)
    while (match) {
      const offset = match.index + match[0].length
      indices.push([match.index, offset])
      match = re.exec(text)
    }
    return indices
  }
  const searchStringLen = searchStringOrRegex.length

  if (searchStringLen === 0) {
    return []
  }

  const indices = []

  let strCpy = text
  let searchStringCpy = searchStringOrRegex
  if (!caseSensitive) {
    strCpy = text.toLocaleLowerCase()
    searchStringCpy = searchStringOrRegex.toLocaleLowerCase()
  }

  if (!diacriticsSensitive) {
    strCpy = diacritics.remove(strCpy)
    searchStringCpy = diacritics.remove(searchStringCpy)
  }

  let startIndex = 0
  let index = strCpy.indexOf(searchStringCpy, startIndex)
  while (index > -1) {
    startIndex = index + searchStringLen
    indices.push([index, startIndex])

    index = strCpy.indexOf(searchStringCpy, index + 1)
  }

  if (wholeWordMatch) {
    const strLength = strCpy.length
    return indices.filter((range) => {
      const [start, end] = range
      const idxBefore = start - 1
      const idxAfter = end
      const idxBeforeIsLetterOrDigit = idxBefore > 0 && isLetterOrDigit(strCpy[idxBefore])
      const idxAfterIsLetterOrDigit = idxAfter < strLength && isLetterOrDigit(strCpy[idxAfter])
      return !(idxAfterIsLetterOrDigit || idxBeforeIsLetterOrDigit)
    })
  }

  return indices
}

function mergeRange([...ranges]) {
  if (!ranges.length) return []

  ranges.sort((fir, sec) => {
    if (fir[0] !== sec[0]) return fir[0] - sec[0]
    return fir[1] - sec[1]
  })

  const merged = []

  let curStart = ranges[0][0]
  let curEnd = ranges[0][1]

  ranges.shift()

  ranges.forEach(([start, end]) => {
    if (start >= curEnd) {
      merged.push([curStart, curEnd])
      curStart = start
      curEnd = end
    } else if (end > curEnd) curEnd = end
  })

  merged.push([curStart, curEnd])

  return merged
}

function highlightChunks(
  text,
  queriesOrQuery,
  { caseSensitive = false, diacriticsSensitive = false, wholeWordMatch = false } = {},
) {
  let queries = queriesOrQuery
  if (typeof queriesOrQuery === 'string' || queriesOrQuery instanceof RegExp) {
    queries = [queriesOrQuery]
  } else if (!Array.isArray(queriesOrQuery)) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('queries must be either string, array of strings or regex.')
    } else {
      return []
    }
  }

  const matches = []

  queries.forEach((query) => {
    matches.push(...indicesOf(text, query, { caseSensitive, diacriticsSensitive, wholeWordMatch }))
  })

  const highlights = mergeRange(matches)

  const chunks = []
  let lastEnd = 0

  highlights.forEach(([start, end], index) => {
    if (lastEnd !== start) {
      chunks.push({
        isHighlighted: false,
        text: text.slice(lastEnd, start)
      })
    }
    chunks.push({
      isHighlighted: true,
      text: text.slice(start, end),
      highlightIndex: index
    })

    lastEnd = end
  })

  if (lastEnd !== text.length) {
    chunks.push({
      isHighlighted: false,
      text: text.slice(lastEnd)
    })
  }

  return chunks
}

const classAndStyleTypes = [Object, Array, String]
export default {
  name: 'TextHighlight',
  props: {
    queries: [Array, String, RegExp],
    caseSensitive: Boolean,
    diacriticsSensitive: Boolean,
    wholeWordMatch: Boolean,
    highlightStyle: classAndStyleTypes,
    highlightClass: classAndStyleTypes,
    highlightComponent: {
      type: [String, Object],
      default: 'mark'
    },
    html: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      text: ''
    }
  },
  computed: {
    attributes() {
      return {
        props: this.$attrs,
        on: this.$listeners
      }
    },
    highlights() {
      const {
        text,
        queries,
        caseSensitive,
        diacriticsSensitive,
        wholeWordMatch
      } = this
      return highlightChunks(text, queries, { caseSensitive, diacriticsSensitive, wholeWordMatch })
    }
  },
  beforeMount() {
    if (this.html) {
      this.setTextFromProp()
    } else {
      this.setTextFromSlot()
    }
  },
  beforeUpdate() {
    if (this.html) {
      this.setTextFromProp()
    } else {
      this.setTextFromSlot()
    }
  },
  methods: {
    setTextFromProp() {
      this.text = this.html
    },
    setTextFromSlot() {
      const defaultSlot = this.$slots.default
      if (!defaultSlot) this.text = ''
      else if (defaultSlot[0].tag !== undefined && process.env.NODE_ENV !== 'production') {
        /* eslint-disable-next-line no-console */
        console.warn('children of <text-highlight> must be a plain string.')
        this.text = ''
      } else {
        this.text = defaultSlot[0].text
      }
    }
  },
  /**
   * Unless `h` is given as parameter, testing (Jest) will yield error:
   *    TypeError: unknown: Duplicate declaration "h"
   *    (This is an error on an internal node. Probably an internal error)
   *
   * Seems babel-plugin-transform-vue-jsx make strange behaivor.
   *
   * Related issue: https://github.com/storybooks/storybook/issues/2727
   */
  /* eslint-disable-next-line no-unused-vars */
  render(h) {
    return h('span', this.highlights.map(({ text, isHighlighted, highlightIndex }) => (isHighlighted ? h(this.highlightComponent, {
      class: ['text__highlight', this.highlightClass],
      style: this.highlightStyle,
      key: highlightIndex,
      attrs: {
        ...this.attributes
      },
      props: {
        index: highlightIndex,
        text
      },
      domProps: {
        innerHTML: text
      }
    }) : h('span', {
      key: highlightIndex,
      domProps: {
        innerHTML: text
      }
    }))))
  }
}
</script>

<style lang="scss">
.text__highlight {
  background: rgb(255, 204, 0);
  border-radius: 3px;
}
</style>
